import { FunctionComponent } from 'react'
import styles from './staffHome.module.scss'
import Title from '@/components/_custom/Title/title'
import ProfileIcon from '@mui/icons-material/AccountCircle'
import { useTranslation } from 'react-i18next'
import { Assignment, ListAlt, LockReset, Notifications, Restaurant } from '@mui/icons-material'
import TranslateIcon from '@mui/icons-material/Translate'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { useAuth } from '@/modules/context/AuthContext'
import LogoutIcon from '@mui/icons-material/ExitToApp'
import ArticleIcon from '@mui/icons-material/Article'
import HelpIcon from '@mui/icons-material/Help'
import CachedIcon from '@mui/icons-material/Cached'
import { removeServiceWorker } from '@/modules/utils/serviceWorker'
import Groups from '@mui/icons-material/Groups'
import House from '@mui/icons-material/House'
import { DaycareType } from '@/types/Daycare'
import FamiliesIcon from '@mui/icons-material/SupervisedUserCircle'
import HomeCase from '../HomeCase/homeCase'

interface StaffHomeMobileProps {
    daycareTypeName: string
    backendVersion: string
    frontendVersion: string
    setDialogChangeLanguageOpen: (open: boolean) => void
    setDialogForgottenPasswordOpen: (open: boolean) => void
    setDialogSupportOpen: (open: boolean) => void
}

type accessType = {
    news: boolean
    suggest: boolean
    staff: boolean
    families: boolean
    taskNote: boolean
    meal: boolean
}

const StaffHomeMobile: FunctionComponent<StaffHomeMobileProps> = ({
    daycareTypeName,
    backendVersion,
    frontendVersion,
    setDialogChangeLanguageOpen,
    setDialogForgottenPasswordOpen,
    setDialogSupportOpen
}: StaffHomeMobileProps) => {
    const { t } = useTranslation()

    const { user, logout } = useAuth()

    const navigate: NavigateFunction = useNavigate()

    const access: accessType = {
        news: !!(user?.currentDaycareRights.hasNewsAccess && user.currentDaycareRights.canReadNewsFeed),
        suggest: !!(user?.currentDaycareRights.hasSuggestAccess && user?.currentDaycareRights.canReadSuggestions),
        staff: !!user?.currentDaycareRights.hasStaffAccess && user.currentDaycareRights.canReadStaffRights,
        families: !!(user?.currentDaycareRights.canReadFamilies || user?.currentDaycareRights.canReadContracts),
        taskNote: !!(user?.currentDaycareRights.hasTaskNoteAccess && user.currentDaycareRights.canReadTaskNote),
        meal: !!(user?.currentDaycareRights.hasMealAccess && user.currentDaycareRights.canReadMeal)
    }

    const isRpeOrLaep: boolean =
        user?.currentDaycare?.type === DaycareType.rpe || user?.currentDaycare?.type === DaycareType.laep

    return (
        <div className={styles.PagePosition}>
            <div className={styles.HomePlacement}>
                <Title text={daycareTypeName} className={styles.Title} />
                <div className={styles.CaseContainer}>
                    <HomeCase
                        text={daycareTypeName}
                        icon={<House className={styles.CaseIcon} />}
                        onClick={(): void | Promise<void> => navigate('/daycare')}
                    />
                </div>
                {(access.staff ||
                    access.families ||
                    access.taskNote ||
                    access.news ||
                    access.suggest ||
                    access.meal) && (
                    <div className={styles.AutomaticCaseContainer}>
                        {access.staff && (
                            <HomeCase
                                text={t('staff')}
                                icon={<Groups className={styles.CaseIcon} />}
                                onClick={(): void | Promise<void> => navigate('/staff')}
                                className={styles.AutomaticCase}
                            />
                        )}
                        {access.families && access.news && (
                            <HomeCase
                                text={isRpeOrLaep ? t('publics') : t('families')}
                                icon={<FamiliesIcon className={styles.CaseIcon} />}
                                onClick={(): void | Promise<void> =>
                                    navigate(
                                        isRpeOrLaep
                                            ? '/adults'
                                            : user?.currentDaycareRights.canReadContracts
                                              ? '/childs'
                                              : '/adults'
                                    )
                                }
                                className={styles.AutomaticCase}
                            />
                        )}
                        {access.taskNote && (
                            <HomeCase
                                text={t('tasksAndNotes')}
                                icon={<ListAlt className={styles.CaseIcon} />}
                                onClick={(): void | Promise<void> => navigate('/taskNote')}
                                className={styles.AutomaticCase}
                            />
                        )}
                        {access.suggest && (
                            <HomeCase
                                text={t('suggestions')}
                                icon={<Assignment className={styles.CaseIcon} />}
                                onClick={(): void | Promise<void> => navigate('/suggest')}
                                className={styles.AutomaticCase}
                            />
                        )}
                        {access.meal && (
                            <HomeCase
                                text={t('meal')}
                                icon={<Restaurant className={styles.CaseIcon} />}
                                onClick={(): void | Promise<void> => navigate('/meal')}
                                className={styles.AutomaticCase}
                            />
                        )}
                    </div>
                )}
            </div>
            <div className={styles.HomePlacement}>
                <Title text={t('myAccount')} className={styles.Title} />
                <div className={styles.CaseContainer}>
                    <HomeCase
                        text={t('myProfile')}
                        icon={<ProfileIcon className={styles.CaseIcon} />}
                        onClick={(): void | Promise<void> => navigate('/account/profile')}
                    />
                    <HomeCase
                        text={t('myAlerts')}
                        icon={<Notifications className={styles.CaseIcon} />}
                        onClick={(): void | Promise<void> => navigate('/account/notifications')}
                    />
                </div>
            </div>
            <div className={styles.HomePlacement}>
                <Title text={t('settings')} className={styles.Title} />
                <div className={styles.CaseContainer}>
                    <HomeCase
                        text={t('language')}
                        icon={<TranslateIcon className={styles.CaseIcon} />}
                        onClick={(): void => setDialogChangeLanguageOpen(true)}
                    />
                    <HomeCase
                        text={t('password')}
                        icon={<LockReset className={styles.CaseIcon} />}
                        onClick={(): void => setDialogForgottenPasswordOpen(true)}
                    />
                </div>
                <div className={styles.CaseContainer}>
                    <HomeCase
                        text={t('refresh')}
                        icon={<CachedIcon className={styles.CaseIcon} />}
                        onClick={removeServiceWorker}
                    />
                    <HomeCase text={t('logout')} icon={<LogoutIcon className={styles.CaseIcon} />} onClick={logout} />
                </div>
                <div className={styles.CaseContainer}>
                    <HomeCase
                        text={t('ourArticles')}
                        icon={<ArticleIcon className={styles.CaseIcon} />}
                        href={'https://www.cald.fr/nos-articles/'}
                    />
                    <HomeCase
                        text={t('help')}
                        icon={<HelpIcon className={styles.CaseIcon} />}
                        href={'https://www.cald.fr/aide-structure'}
                    />
                </div>
            </div>
            <div className={styles.Version}>
                <a onClick={() => setDialogSupportOpen(true)}>
                    V{backendVersion}/{frontendVersion}
                </a>
            </div>
        </div>
    )
}

export default StaffHomeMobile
